import { ApiError } from '../identity/types';
import type { JobTitleOption } from '../people/types';
import { buildMockTemplates } from './mockTemplates';
import { mockOccupationDataset, mockOccupationExists } from './mockOccupations';
import type { CustomOptionRow } from './mockReferenceOptions';
import { jobRoleStatusToWire } from './wireMapping';
import {
  GuidedErrorCode,
  JobRoleErrorCode,
  JobRoleStatus,
  JobRoleVersionStatus,
  emptyJobRoleContent,
  normalizeJobRoleContent,
  type CreateEmployeeJobRoleAssignmentInput,
  type CreateFromTemplateInput,
  type CreateJobRoleInput,
  type DuplicateJobRoleInput,
  type EmployeeJobRoleAssignment,
  type JobRoleAuditItem,
  type JobRoleContent,
  type JobRoleTemplateDetail,
  type PayBand,
  type SetPayBandInput,
  type UpdateJobRoleInput,
  DEFAULT_ANNUAL_HOURS,
  MAX_ANNUAL_HOURS,
} from './types';

/**
 * The in-memory company data behind MockJobRoleGateway AND MockPeopleGateway (so a role created, archived or assigned on one
 * screen is what every other mock screen sees). It plays the SERVER: it owns the rules the real API enforces (only a Draft is
 * editable, archive instead of delete, one Draft per role, a stale concurrency version is refused, a Draft or Archived role
 * cannot be assigned). The UI never relies on any of this being re-checked client-side. Nothing here touches Identity, a
 * membership or a permission: a job role never grants access.
 */

export interface StoredVersion {
  id: string;
  versionNumber: number;
  status: number;
  effectiveFromUtc: string | null;
  effectiveToUtc: string | null;
  createdByUserId: string;
  createdAtUtc: string;
  publishedAtUtc: string | null;
  content: JobRoleContent;
}

export interface StoredRole {
  id: string;
  roleCode: string | null;
  title: string;
  description?: string | null;
  departmentId: string | null;
  jobFamily: string | null;
  careerLevel: string | null;
  managementLevel: string | null;
  socCode: string | null;
  onetCode: string | null;
  status: number;
  sourceTemplateId: string | null;
  concurrencyVersion: number;
  createdAtUtc: string;
  updatedAtUtc: string;
  versions: StoredVersion[];
}

const SEED_USER = 'admin@epn.local';
const SEED_USER_ID = '88888888-8888-4888-8888-888888888801';

/** Deterministic GUID-shaped ids for the mock, reset with the store. */
let guidSeq = 0;
const guid = (): string => `00000000-0000-4000-8000-${String(++guidSeq).padStart(12, '0')}`;

type StoredAssignment = Omit<EmployeeJobRoleAssignment, 'roleStatus'>;

export function problem(status: number, detail: string, code: string | null = null): ApiError {
  return new ApiError(status, detail, null, true, code);
}

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
const cleanName = (value: string) => value.split(/\s+/).filter(Boolean).join(' ');
const nullable = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

function isoDay(day: string): string {
  return `${day}T09:00:00.000Z`;
}

/** What an Active role needs before it can be published. The real rules live on the server; this mirrors them closely enough for the UI. */
export function activationProblem(role: Pick<StoredRole, 'title'>, content: JobRoleContent): string | null {
  if (!role.title.trim()) return 'A role needs a title before it can be activated.';
  if (!content.summary?.trim()) return 'Add a summary before activating this role.';
  if (content.essentialDuties.length === 0) return 'Add at least one essential duty before activating this role.';
  return null;
}

export class MockJobRoleStore {
  private roles: StoredRole[] = [];
  private templates: JobRoleTemplateDetail[] = [];
  private assignments = new Map<string, StoredAssignment[]>();
  private audit = new Map<string, JobRoleAuditItem[]>();
  /** Employer pay bands per role, NEWEST FIRST and append-only: a change end-dates the current row and adds a new one. */
  private bands = new Map<string, PayBand[]>();
  /** The company's own reference option values (guided fields). Standard options live in mockReferenceData.ts and are never stored per company. */
  private customOptions: CustomOptionRow[] = [];
  private seq = 0;

  constructor() {
    this.reset();
  }

  /** Test helper: the store is a module-level singleton, so one test's changes must not leak into the next. */
  reset(): void {
    this.seq = 0;
    guidSeq = 0;
    this.assignments = new Map();
    this.audit = new Map();
    this.bands = new Map();
    this.customOptions = [];
    this.templates = buildMockTemplates();
    this.roles = [];

    // Legacy roles: they predate the library (a plain Job Title), so they have no version and render as a minimal role.
    this.seedLegacy('j-mgr', 'Manager', 'd-ops');
    this.seedLegacy('j-acct', 'Accountant', 'd-fin');

    const dept: Record<string, string> = {
      'Payroll Specialist': 'd-fin', 'Senior Payroll Specialist': 'd-fin', 'Payroll Manager': 'd-fin', 'Staff Accountant': 'd-fin',
      'Human Resources Coordinator': 'd-hr', 'Operations Manager': 'd-ops', 'Customer Support Specialist': 'd-support',
      'Chief Financial Officer': 'd-fin',
    };
    const tpl = (title: string) => this.templates.find((t) => t.title === title)!;
    const seed = (title: string, status: number, opts: { draftOnly?: boolean; twoVersions?: boolean; openDraft?: boolean } = {}) => {
      const t = tpl(title);
      const published = (n: number, from: string, to: string | null, content: JobRoleContent): StoredVersion => ({
        versionNumber: n, status: to ? JobRoleVersionStatus.Superseded : JobRoleVersionStatus.Published, effectiveFromUtc: isoDay(from),
        effectiveToUtc: to ? isoDay(to) : null, id: guid(), createdByUserId: SEED_USER_ID, createdAtUtc: isoDay(from), publishedAtUtc: isoDay(from), content,
      });
      const versions: StoredVersion[] = [];
      if (opts.draftOnly) {
        versions.push({
          versionNumber: 1, status: JobRoleVersionStatus.Draft, effectiveFromUtc: null, effectiveToUtc: null, id: guid(), createdByUserId: SEED_USER_ID,
          createdAtUtc: isoDay('2026-09-20'), publishedAtUtc: null, content: clone(t.content),
        });
      } else if (opts.twoVersions) {
        const first = clone(t.content);
        first.essentialDuties = first.essentialDuties.slice(0, 2);
        versions.push(published(1, '2025-06-01', '2026-01-15', first));
        versions.push(published(2, '2026-01-15', null, clone(t.content)));
      } else {
        versions.push(published(1, '2026-01-15', null, clone(t.content)));
      }
      if (opts.openDraft) {
        const next = clone(t.content);
        next.summary = `${t.content.summary ?? ''} (proposed update)`;
        versions.push({
          versionNumber: versions.length + 1, status: JobRoleVersionStatus.Draft, effectiveFromUtc: null, effectiveToUtc: null,
          id: guid(), createdByUserId: SEED_USER_ID, createdAtUtc: isoDay('2026-09-28'), publishedAtUtc: null, content: next,
        });
      }
      const slug = t.id.replace(/^tpl-/, '');
      const role: StoredRole = {
        id: `jr-${slug}`, roleCode: t.code, title: t.title, departmentId: dept[title] ?? null, jobFamily: t.jobFamily, careerLevel: t.careerLevel,
        managementLevel: null, socCode: t.socCode, onetCode: t.onetCode, status, sourceTemplateId: t.id, concurrencyVersion: 1 + versions.length,
        createdAtUtc: versions[0]!.createdAtUtc, updatedAtUtc: versions[versions.length - 1]!.createdAtUtc, versions,
      };
      this.roles.push(role);
      this.addAudit(role.id, 'JobRoleCopiedFromTemplate', [], versions[0]!.createdAtUtc);
      if (!opts.draftOnly) this.addAudit(role.id, 'JobRoleActivated', [], versions.find((v) => v.publishedAtUtc)!.publishedAtUtc!);
      if (opts.twoVersions) this.addAudit(role.id, 'JobRoleVersionCreated', [], isoDay('2026-01-10'));
      if (status === JobRoleStatus.Archived) this.addAudit(role.id, 'JobRoleArchived', [], isoDay('2026-08-01'));
    };

    seed('Payroll Specialist', JobRoleStatus.Active, { twoVersions: true });
    seed('Senior Payroll Specialist', JobRoleStatus.Active);
    seed('Payroll Manager', JobRoleStatus.Active, { openDraft: true });
    seed('Staff Accountant', JobRoleStatus.Active);
    seed('Human Resources Coordinator', JobRoleStatus.Draft, { draftOnly: true });
    seed('Operations Manager', JobRoleStatus.Active);
    seed('Customer Support Specialist', JobRoleStatus.Archived);
    seed('Chief Financial Officer', JobRoleStatus.Active);

    // Employer pay bands: the COMPANY's own policy for two roles (demo figures), one of them with a superseded earlier band so the
    // history list has something to show. Nothing here is market data.
    const band = (roleId: string, b: Omit<PayBand, 'id' | 'roleId' | 'currency' | 'createdAtUtc' | 'acknowledgedAtUtc'>) => {
      const at = isoDay(b.effectiveFromDate);
      return { ...b, id: guid(), roleId, currency: 'USD', createdAtUtc: at, acknowledgedAtUtc: at } satisfies PayBand;
    };
    this.bands.set('jr-payroll-specialist', [
      band('jr-payroll-specialist', { basis: 'Hourly', minimum: 24, midpoint: 28, maximum: 32, annualHoursAssumption: 2080, effectiveFromDate: '2026-01-15', effectiveToDate: null, note: 'Reviewed with the annual pay review.', concurrencyVersion: 2 }),
      band('jr-payroll-specialist', { basis: 'Hourly', minimum: 22, midpoint: 26, maximum: 30, annualHoursAssumption: 2080, effectiveFromDate: '2025-01-01', effectiveToDate: '2026-01-15', note: null, concurrencyVersion: 1 }),
    ]);
    this.bands.set('jr-staff-accountant', [
      band('jr-staff-accountant', { basis: 'Annual', minimum: 55000, midpoint: 65000, maximum: 75000, annualHoursAssumption: 2080, effectiveFromDate: '2026-01-15', effectiveToDate: null, note: null, concurrencyVersion: 1 }),
    ]);
  }

  // ---- employer pay bands (company data; never an employee's pay) ----

  currentBand(roleId: string): PayBand | undefined {
    return (this.bands.get(roleId) ?? []).find((b) => b.effectiveToDate === null);
  }

  bandHistory(roleId: string): PayBand[] {
    return (this.bands.get(roleId) ?? []).map((b) => ({ ...b }));
  }

  /** The server's rules: review acknowledgement, 0 < min <= mid <= max, hours 1..8784, no future effective date, no new band on an Archived role,
   * and the optimistic-concurrency check when a current band exists. A change ends the current row and appends a new one in one step. */
  setBand(roleId: string, input: SetPayBandInput, today = new Date().toISOString().slice(0, 10)): PayBand {
    const role = this.requireRole(roleId);
    if (role.status === JobRoleStatus.Archived) throw problem(409, 'An archived role cannot get a new pay band. Restore the role first.', JobRoleErrorCode.RoleNotEditable);
    if (input.reviewAcknowledged !== true) throw problem(400, 'Confirm that you have reviewed this pay band before saving it.', JobRoleErrorCode.PayBandReviewRequired);
    if (input.basis !== 'Hourly' && input.basis !== 'Annual') throw problem(400, 'Choose hourly or annual for the pay band.', 'PAY_BAND_INVALID');
    const { minimum, midpoint, maximum } = input;
    if (!(minimum > 0 && minimum <= midpoint && midpoint <= maximum)) {
      throw problem(400, 'The pay band must satisfy 0 < minimum <= midpoint <= maximum.', 'PAY_BAND_INVALID');
    }
    const hours = input.annualHoursAssumption ?? DEFAULT_ANNUAL_HOURS;
    if (!(hours >= 1 && hours <= MAX_ANNUAL_HOURS)) throw problem(400, `The annual hours must be between 1 and ${MAX_ANNUAL_HOURS}.`, 'PAY_BAND_INVALID');
    const note = nullable(input.note);
    if (note && note.length > 500) throw problem(400, 'The note can be at most 500 characters.', 'PAY_BAND_INVALID');
    const effectiveFromDate = input.effectiveFromDate ?? today;
    if (effectiveFromDate > today) throw problem(400, 'The effective date cannot be in the future.', 'PAY_BAND_INVALID');

    const history = this.bands.get(roleId) ?? [];
    const current = history.find((b) => b.effectiveToDate === null);
    if (current && input.expectedConcurrencyVersion !== current.concurrencyVersion) {
      throw problem(409, 'This pay band was changed by someone else after you opened it. Reload it to see the latest version.', JobRoleErrorCode.ConcurrencyConflict);
    }
    if (current) current.effectiveToDate = effectiveFromDate;
    const now = new Date().toISOString();
    const created: PayBand = {
      id: guid(), roleId, basis: input.basis, currency: 'USD', minimum, midpoint, maximum, annualHoursAssumption: hours, effectiveFromDate,
      effectiveToDate: null, note, concurrencyVersion: current ? current.concurrencyVersion + 1 : 1, createdAtUtc: now, acknowledgedAtUtc: now,
    };
    this.bands.set(roleId, [created, ...history]);
    this.addAudit(roleId, current ? 'JobRolePayBandChanged' : 'JobRolePayBandSet', []);
    return { ...created };
  }

  /** Ends the current band (end-dated, never deleted). */
  endBand(roleId: string, expected: number | undefined, today = new Date().toISOString().slice(0, 10)): void {
    this.requireRole(roleId);
    const current = this.currentBand(roleId);
    if (!current) throw problem(404, 'This role has no current pay band.', 'PAY_BAND_NOT_FOUND');
    if (expected !== current.concurrencyVersion) {
      throw problem(409, 'This pay band was changed by someone else after you opened it. Reload it to see the latest version.', JobRoleErrorCode.ConcurrencyConflict);
    }
    current.effectiveToDate = today;
    this.addAudit(roleId, 'JobRolePayBandEnded', []);
  }

  private seedLegacy(id: string, name: string, departmentId: string) {
    this.roles.push({
      id, roleCode: null, title: name, departmentId, jobFamily: null, careerLevel: null, managementLevel: null, socCode: null, onetCode: null,
      status: JobRoleStatus.Active, sourceTemplateId: null, concurrencyVersion: 1, createdAtUtc: isoDay('2023-01-01'), updatedAtUtc: isoDay('2023-01-01'),
      versions: [],
    });
  }

  // ---- reads ----

  allRoles(): StoredRole[] {
    return this.roles;
  }

  role(id: string): StoredRole | undefined {
    return this.roles.find((r) => r.id === id);
  }

  requireRole(id: string): StoredRole {
    const found = this.role(id);
    if (!found) throw problem(404, 'Role not found.');
    return found;
  }

  allTemplates(): JobRoleTemplateDetail[] {
    return this.templates;
  }

  template(id: string): JobRoleTemplateDetail {
    const found = this.templates.find((t) => t.id === id);
    if (!found) throw problem(404, 'Template not found.');
    return found;
  }

  publishedVersion(role: StoredRole): StoredVersion | undefined {
    return role.versions.find((v) => v.status === JobRoleVersionStatus.Published);
  }

  draftVersion(role: StoredRole): StoredVersion | undefined {
    return role.versions.find((v) => v.status === JobRoleVersionStatus.Draft);
  }

  /** What `GET /api/job-titles` returns: ACTIVE roles only, so a Draft or Archived role never reaches Add Employee. */
  listActiveTitles(): JobTitleOption[] {
    return this.roles
      .filter((r) => r.status === JobRoleStatus.Active)
      .map((r) => ({ id: r.id, name: r.title, departmentId: r.departmentId, isActive: true, ...(r.roleCode ? { roleCode: r.roleCode } : {}), ...(r.jobFamily ? { jobFamily: r.jobFamily } : {}) }));
  }

  // ---- writes ----

  /** The legacy get-or-create-by-name `POST /api/job-titles`: it now creates an ACTIVE minimal role (no version). */
  createLegacyTitle(name: string, departmentId: string | null): JobTitleOption {
    const clean = cleanName(name);
    if (!clean) throw problem(400, 'Job title is required.');
    const existing = this.roles.find((r) => r.departmentId === departmentId && r.title.toLowerCase() === clean.toLowerCase());
    if (existing && existing.status === JobRoleStatus.Active) {
      return { id: existing.id, name: existing.title, departmentId: existing.departmentId, isActive: true };
    }
    if (existing) throw problem(409, 'A role with that title already exists in this department but is not Active.');
    const now = new Date().toISOString();
    const role: StoredRole = {
      id: `j-${crypto.randomUUID()}`, roleCode: null, title: clean, departmentId, jobFamily: null, careerLevel: null, managementLevel: null,
      socCode: null, onetCode: null, status: JobRoleStatus.Active, sourceTemplateId: null, concurrencyVersion: 1, createdAtUtc: now, updatedAtUtc: now,
      versions: [],
    };
    this.roles = [...this.roles, role];
    this.addAudit(role.id, 'JobRoleCreated', ['title', 'departmentId']);
    return { id: role.id, name: role.title, departmentId, isActive: true };
  }

  // ---- guided fields: company reference options and the occupation classification rules of the server ----

  customReferenceOptions(): CustomOptionRow[] {
    return this.customOptions;
  }

  addCustomReferenceOption(row: CustomOptionRow): void {
    this.customOptions = [...this.customOptions, row];
  }

  /**
   * The server's SOC / O*NET rules (contract 2.1): a non-blank code must have the right format (400 OCCUPATION_CODE_INVALID) and, when a dataset
   * exists, be in it (400 OCCUPATION_CODE_UNKNOWN) unless it equals the role's currently stored value (a legacy value is preserved, never blocked);
   * `NotYetClassified` needs a reason of at most 300 characters and a blank code, `Confirmed` needs a code (400 CLASSIFICATION_INVALID); and the dataset
   * version is STAMPED here from the current dataset: whatever the client sent is ignored. Returns the content with the stamped classification.
   */
  private checkClassification(
    header: { socCode?: string | null | undefined; onetCode?: string | null | undefined },
    content: JobRoleContent,
    stored?: { socCode: string | null; onetCode: string | null },
  ): JobRoleContent {
    const soc = nullable(header.socCode);
    const onet = nullable(header.onetCode);
    if (soc && soc !== stored?.socCode && !/^\d{2}-\d{4}$/.test(soc)) throw problem(400, 'The SOC code must look like 43-3051.', GuidedErrorCode.OccupationCodeInvalid);
    if (onet && onet !== stored?.onetCode && !/^\d{2}-\d{4}\.\d{2}$/.test(onet)) throw problem(400, 'The O*NET code must look like 43-3051.00.', GuidedErrorCode.OccupationCodeInvalid);
    if (soc && soc !== stored?.socCode && !mockOccupationExists('Soc', soc)) throw problem(400, 'That SOC code is not in the official occupation list. Search for it and choose it from the list.', GuidedErrorCode.OccupationCodeUnknown);
    if (onet && onet !== stored?.onetCode && !mockOccupationExists('Onet', onet)) throw problem(400, 'That O*NET code is not in the official occupation list. Search for it and choose it from the list.', GuidedErrorCode.OccupationCodeUnknown);

    const c = normalizeJobRoleContent(content);
    const cls = { ...c.classification };
    const check = (system: 'Soc' | 'Onet', code: string | null) => {
      const prefix = system === 'Soc' ? 'soc' : 'onet';
      const status = cls[`${prefix}Status`];
      const reason = nullable(cls[`${prefix}NotYetClassifiedReason`]);
      const label = system === 'Soc' ? 'SOC' : 'O*NET';
      if (status === 'NotYetClassified') {
        if (!reason) throw problem(400, `Say why the ${label} code is not yet classified.`, GuidedErrorCode.ClassificationInvalid);
        if (reason.length > 300) throw problem(400, `The reason must be 300 characters or fewer.`, GuidedErrorCode.ClassificationInvalid);
        if (code) throw problem(400, `A role marked not yet classified cannot also have a ${label} code.`, GuidedErrorCode.ClassificationInvalid);
      } else if (status === 'Confirmed' && !code) {
        throw problem(400, `Choose the ${label} code, or mark it not yet classified.`, GuidedErrorCode.ClassificationInvalid);
      }
      cls[`${prefix}NotYetClassifiedReason`] = status === 'NotYetClassified' ? reason : null;
      cls[`${prefix}DatasetVersion`] = status === 'Confirmed' ? (mockOccupationDataset(system)?.version ?? null) : null;
    };
    check('Soc', soc);
    check('Onet', onet);
    if ((c.budgetAuthority ?? '').length > 500) throw problem(400, 'The budget authority text must be 500 characters or fewer.');
    return { ...c, classification: cls };
  }

  private assertUnique(title: string, departmentId: string | null, roleCode: string | null, exceptId?: string) {
    const clash = this.roles.find((r) => r.id !== exceptId && r.departmentId === departmentId && r.title.toLowerCase() === title.toLowerCase());
    if (clash) throw problem(400, 'A role with that title already exists in this department.', 'DUPLICATE_ROLE_TITLE');
    if (roleCode) {
      const codeClash = this.roles.find((r) => r.id !== exceptId && r.roleCode?.toLowerCase() === roleCode.toLowerCase());
      if (codeClash) throw problem(400, 'That internal code is already used by another role in this company.', 'DUPLICATE_ROLE_CODE');
    }
  }

  private newDraftRole(
    header: { title: string; description?: string | null | undefined; roleCode?: string | null | undefined; departmentId?: string | null | undefined; jobFamily?: string | null | undefined;
      careerLevel?: string | null | undefined; managementLevel?: string | null | undefined; socCode?: string | null | undefined; onetCode?: string | null | undefined },
    content: JobRoleContent,
    sourceTemplateId: string | null,
    eventType: string,
    storedCodes?: { socCode: string | null; onetCode: string | null },
  ): StoredRole {
    const title = cleanName(header.title ?? '');
    if (!title) throw problem(400, 'A role needs a title.');
    const departmentId = header.departmentId || null;
    const roleCode = nullable(header.roleCode);
    this.assertUnique(title, departmentId, roleCode);
    content = this.checkClassification(header, content, storedCodes);
    const now = new Date().toISOString();
    const role: StoredRole = {
      id: `jr-${crypto.randomUUID()}`, roleCode, title, description: nullable(header.description), departmentId, jobFamily: nullable(header.jobFamily), careerLevel: nullable(header.careerLevel),
      managementLevel: nullable(header.managementLevel), socCode: nullable(header.socCode), onetCode: nullable(header.onetCode),
      status: JobRoleStatus.Draft, sourceTemplateId, concurrencyVersion: 1, createdAtUtc: now, updatedAtUtc: now,
      versions: [{
        versionNumber: 1, status: JobRoleVersionStatus.Draft, effectiveFromUtc: null, effectiveToUtc: null, id: guid(), createdByUserId: SEED_USER_ID, createdAtUtc: now,
        publishedAtUtc: null, content,
      }],
    };
    this.roles = [...this.roles, role];
    this.addAudit(role.id, eventType, ['title']);
    return role;
  }

  createRole(input: CreateJobRoleInput): StoredRole {
    return this.newDraftRole(input, normalizeJobRoleContent(input.content ?? emptyJobRoleContent()), null, 'JobRoleCreated');
  }

  createFromTemplate(templateId: string, input: CreateFromTemplateInput = {}): StoredRole {
    const template = this.template(templateId);
    // The template's content is COPIED: the global template is never referenced or changed by the company's role.
    return this.newDraftRole(
      {
        title: input.title || template.title, roleCode: input.roleCode ?? null, departmentId: input.departmentId ?? null, jobFamily: template.jobFamily,
        careerLevel: template.careerLevel, socCode: template.socCode, onetCode: template.onetCode,
      },
      clone(template.content),
      template.id,
      'JobRoleCopiedFromTemplate',
    );
  }

  duplicate(id: string, input: DuplicateJobRoleInput): StoredRole {
    const source = this.requireRole(id);
    const version = this.draftVersion(source) ?? this.publishedVersion(source) ?? source.versions[source.versions.length - 1];
    const title = cleanName(input.title ?? '');
    if (!title) throw problem(400, 'A copy needs a new title.');
    return this.newDraftRole(
      { title, roleCode: input.roleCode ?? null, departmentId: source.departmentId, jobFamily: source.jobFamily, careerLevel: source.careerLevel,
        managementLevel: source.managementLevel, socCode: source.socCode, onetCode: source.onetCode },
      clone(version?.content ?? emptyJobRoleContent()),
      source.sourceTemplateId,
      'JobRoleDuplicated',
      { socCode: source.socCode, onetCode: source.onetCode },
    );
  }

  private assertVersion(role: StoredRole, expected: number | undefined) {
    if (expected === undefined || expected !== role.concurrencyVersion) {
      throw problem(409, 'This role was changed by someone else after you opened it. Reload it to see the latest version.', JobRoleErrorCode.ConcurrencyConflict);
    }
  }

  update(id: string, input: UpdateJobRoleInput): StoredRole {
    const role = this.requireRole(id);
    const draft = this.draftVersion(role);
    if (!draft) throw problem(409, 'Only a Draft can be edited. Create a new version to change an active role.', JobRoleErrorCode.NotDraft);
    this.assertVersion(role, input.expectedConcurrencyVersion);
    const title = cleanName(input.title ?? '');
    if (!title) throw problem(400, 'A role needs a title.');
    const departmentId = input.departmentId || null;
    const roleCode = nullable(input.roleCode);
    this.assertUnique(title, departmentId, roleCode, role.id);
    // Checked against the codes STORED NOW: a value the role already has is a legacy value, kept and never blocked.
    const content = this.checkClassification(input, normalizeJobRoleContent(input.content), { socCode: role.socCode, onetCode: role.onetCode });
    const changed: string[] = [];
    const set = <K extends keyof StoredRole>(key: K, value: StoredRole[K]) => {
      if (role[key] !== value) {
        role[key] = value;
        changed.push(String(key));
      }
    };
    set('title', title);
    set('description', nullable(input.description));
    set('roleCode', roleCode);
    set('departmentId', departmentId);
    set('jobFamily', nullable(input.jobFamily));
    set('careerLevel', nullable(input.careerLevel));
    set('managementLevel', nullable(input.managementLevel));
    set('socCode', nullable(input.socCode));
    set('onetCode', nullable(input.onetCode));
    if (JSON.stringify(content) !== JSON.stringify(draft.content)) changed.push('content');
    draft.content = content;
    role.concurrencyVersion += 1;
    role.updatedAtUtc = new Date().toISOString();
    this.addAudit(role.id, 'JobRoleUpdated', changed);
    return role;
  }

  /** The optional concurrency check of create-version, archive and restore: checked only when the caller sent one. */
  private checkIfSent(role: StoredRole, expected: number | undefined) {
    if (expected !== undefined) this.assertVersion(role, expected);
  }

  createVersion(id: string, expected?: number): StoredRole {
    const role = this.requireRole(id);
    this.checkIfSent(role, expected);
    if (role.status === JobRoleStatus.Archived) throw problem(409, 'An archived role must be restored before a new version can be created.', 'ROLE_ARCHIVED');
    if (this.draftVersion(role)) throw problem(409, 'This role already has a draft version. Open it to keep editing.', 'DRAFT_EXISTS');
    const base = this.publishedVersion(role);
    const now = new Date().toISOString();
    role.versions = [...role.versions, {
      versionNumber: Math.max(0, ...role.versions.map((v) => v.versionNumber)) + 1, status: JobRoleVersionStatus.Draft, effectiveFromUtc: null,
      effectiveToUtc: null, id: guid(), createdByUserId: SEED_USER_ID, createdAtUtc: now, publishedAtUtc: null, content: clone(base?.content ?? emptyJobRoleContent()),
    }];
    role.concurrencyVersion += 1;
    role.updatedAtUtc = now;
    this.addAudit(role.id, 'JobRoleVersionCreated', []);
    return role;
  }

  activate(id: string, expected: number | undefined): StoredRole {
    const role = this.requireRole(id);
    const draft = this.draftVersion(role);
    if (!draft) throw problem(409, 'There is no draft to activate.', JobRoleErrorCode.NotDraft);
    this.assertVersion(role, expected);
    const issue = activationProblem(role, draft.content);
    if (issue) throw problem(400, issue, 'ROLE_INCOMPLETE');
    const now = new Date().toISOString();
    for (const v of role.versions) {
      if (v.status === JobRoleVersionStatus.Published) {
        v.status = JobRoleVersionStatus.Superseded;
        v.effectiveToUtc = now;
      }
    }
    draft.status = JobRoleVersionStatus.Published;
    draft.effectiveFromUtc = now;
    draft.publishedAtUtc = now;
    role.status = JobRoleStatus.Active;
    role.concurrencyVersion += 1;
    role.updatedAtUtc = now;
    this.addAudit(role.id, 'JobRoleActivated', []);
    return role;
  }

  archive(id: string, expected?: number): StoredRole {
    const role = this.requireRole(id);
    this.checkIfSent(role, expected);
    if (role.status === JobRoleStatus.Archived) throw problem(409, 'This role is already archived.', 'ROLE_ALREADY_ARCHIVED');
    role.status = JobRoleStatus.Archived;
    role.concurrencyVersion += 1;
    role.updatedAtUtc = new Date().toISOString();
    this.addAudit(role.id, 'JobRoleArchived', []);
    return role;
  }

  restore(id: string, expected?: number): StoredRole {
    const role = this.requireRole(id);
    this.checkIfSent(role, expected);
    if (role.status !== JobRoleStatus.Archived) throw problem(409, 'Only an archived role can be restored.', 'ROLE_NOT_ARCHIVED');
    if (!this.publishedVersion(role)) throw problem(409, 'This role has no published version, so it cannot be restored. Duplicate it or activate its draft instead.', 'ROLE_NOT_RESTORABLE');
    role.status = JobRoleStatus.Active;
    role.concurrencyVersion += 1;
    role.updatedAtUtc = new Date().toISOString();
    this.addAudit(role.id, 'JobRoleRestored', []);
    return role;
  }

  // ---- assignments (append-only history; never touches Identity or any permission) ----

  assign(employeeId: string, input: CreateEmployeeJobRoleAssignmentInput, today = new Date().toISOString().slice(0, 10)): StoredAssignment {
    const role = this.role(input.jobTitleId);
    if (!role) throw problem(404, 'Role not found.');
    if (role.status !== JobRoleStatus.Active) {
      throw problem(409, 'Only an Active role can be assigned to an employee.', JobRoleErrorCode.NotAssignable);
    }
    const effectiveFrom = input.effectiveFromDate || today;
    const history = this.assignments.get(employeeId) ?? [];
    const open = history.find((a) => a.effectiveTo === null);
    if (open?.jobTitleId === role.id) return open;
    if (open) open.effectiveTo = effectiveFrom;
    const version = this.publishedVersion(role);
    const item: StoredAssignment = {
      id: guid(), jobTitleId: role.id, roleTitle: role.title, roleCode: role.roleCode, jobRoleVersionId: version?.id ?? null,
      versionNumber: version?.versionNumber ?? null, effectiveFrom, effectiveTo: null, isPrimary: true, assignedByUserId: SEED_USER_ID,
      reason: nullable(input.reason),
    };
    this.assignments.set(employeeId, [item, ...history]);
    this.addAudit(role.id, open ? 'EmployeeJobRoleChanged' : 'EmployeeJobRoleAssigned', []);
    return item;
  }

  /** Ends the employee's open assignment (their role was cleared on the profile). History is kept, nothing is deleted. */
  endOpenAssignment(employeeId: string, on = new Date().toISOString().slice(0, 10)): void {
    const open = (this.assignments.get(employeeId) ?? []).find((a) => a.effectiveTo === null);
    if (open) open.effectiveTo = on;
  }

  /** Newest first. */
  assignmentsFor(employeeId: string): EmployeeJobRoleAssignment[] {
    return (this.assignments.get(employeeId) ?? []).map((a) => ({
      ...a, roleStatus: jobRoleStatusToWire(this.role(a.jobTitleId)?.status ?? 0),
    }));
  }

  /** Employees whose assignment history shows this role: current employees come from the People mock, which owns `jobTitleId`. */
  assignedSince(roleId: string, employeeId: string): string | null {
    return (this.assignments.get(employeeId) ?? []).find((a) => a.jobTitleId === roleId && a.effectiveTo === null)?.effectiveFrom ?? null;
  }

  // ---- audit ----

  private addAudit(roleId: string, eventType: string, changedFields: string[], at = new Date().toISOString()) {
    const list = this.audit.get(roleId) ?? [];
    // Like the real API: ids and changed field NAMES (PascalCase) only, never values.
    const names = changedFields.map((f) => f.charAt(0).toUpperCase() + f.slice(1));
    list.unshift({
      id: guid(), occurredAtUtc: at, actorUserId: SEED_USER_ID, actorEmailSnapshot: SEED_USER, eventType,
      metadataJson: JSON.stringify({ roleId, ...(names.length > 0 ? { changedFields: names } : {}) }),
    });
    // Newest first, whichever order the events were added in (the seeds add older events after newer ones).
    list.sort((a, b) => b.occurredAtUtc.localeCompare(a.occurredAtUtc));
    this.audit.set(roleId, list);
  }

  auditFor(roleId: string): JobRoleAuditItem[] {
    return (this.audit.get(roleId) ?? []).map((e) => ({ ...e }));
  }
}

export const mockJobRoleStore = new MockJobRoleStore();
